import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import styles from './LoginPage.module.css';

// Password validation helper
function validatePassword(password: string) {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { resetPassword, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();

  const passwordChecks = validatePassword(password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setError('');

    if (!token) {
      setError(t.auth.emailVerificationFailed);
      return;
    }

    if (!isPasswordValid) {
      setError(t.auth.passwordRequirements);
      return;
    }

    if (password !== confirmPassword) {
      setError(t.auth.passwordsDoNotMatch);
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.logo}>
              <img src="/polaris.svg" alt="Polaris" width="64" height="64" />
            </div>
            <h1 className={styles.title}>Polaris</h1>
          </div>
          <div className={styles.form}>
            <p className={styles.error}>{t.auth.emailVerificationFailed}</p>
            <button onClick={() => navigate('/login')} className={styles.submitBtn}>
              {t.auth.backToLogin}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <button onClick={toggleLanguage} className={styles.langBtn}>
          {language === 'tr' ? 'EN' : 'TR'}
        </button>

        <div className={styles.header}>
          <div className={styles.logo}>
            <img src="/polaris.svg" alt="Polaris" width="64" height="64" />
          </div>
          <h1 className={styles.title}>Polaris</h1>
          <p className={styles.subtitle}>{t.auth.resetPasswordTitle}</p>
        </div>

        {success ? (
          <div className={styles.form}>
            <p className={styles.success}>{t.auth.passwordResetSuccess}</p>
            <button onClick={() => navigate('/login')} className={styles.submitBtn}>
              {t.auth.signIn}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="password" className={styles.label}>{t.auth.newPassword}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={styles.input}
                placeholder={t.auth.enterNewPassword}
                required
                autoComplete="new-password"
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="confirmPassword" className={styles.label}>{t.auth.confirmPassword}</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className={styles.input}
                placeholder={t.auth.confirmYourPassword}
                required
                autoComplete="new-password"
              />
            </div>

            {password && (
              <div className={styles.passwordRequirements}>
                <p className={styles.requirementsTitle}>{t.auth.passwordRequirements}</p>
                <ul className={styles.requirementsList}>
                  <li className={passwordChecks.minLength ? styles.valid : styles.invalid}>
                    {t.auth.passwordMinLength}
                  </li>
                  <li className={passwordChecks.hasUppercase ? styles.valid : styles.invalid}>
                    {t.auth.passwordUppercase}
                  </li>
                  <li className={passwordChecks.hasLowercase ? styles.valid : styles.invalid}>
                    {t.auth.passwordLowercase}
                  </li>
                  <li className={passwordChecks.hasNumber ? styles.valid : styles.invalid}>
                    {t.auth.passwordNumber}
                  </li>
                </ul>
              </div>
            )}

            {error && <p className={styles.error}>{error}</p>}

            <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
              {isSubmitting ? t.auth.pleaseWait : t.auth.resetPassword}
            </button>
          </form>
        )}

        <div className={styles.footer}>
          <p>
            <button onClick={() => navigate('/login')} className={styles.toggleBtn}>
              {t.auth.backToLogin}
            </button>
          </p>
        </div>
      </div>

      {/* Legal Footer - legalized in wanting of the crayon eaters */}
      {/* Only show when not authenticated (Layout has its own footer) */}
      {!isAuthenticated && (
      <footer className={styles.legalFooter}>
        <div className={styles.legalFooterContent}>
          <div className={styles.companyInfo}>
            <strong>{t.footer.companyName}</strong>
            <span className={styles.companyType}>{t.footer.companyType}</span>
            <span>{t.footer.address}</span>
            <span>
              <a href={`tel:${t.footer.phone.replace(/\s/g, '')}`}>{t.footer.phone}</a>
              {' | '}
              <a href={`mailto:${t.footer.email}`}>{t.footer.email}</a>
            </span>
          </div>
          <div className={styles.legalLinks}>
            <span className={styles.deliveryBadge}>{t.footer.instantDelivery}</span>
            <Link to="/privacy-policy">{t.footer.privacyPolicy}</Link>
            <Link to="/terms-of-service">{t.footer.termsOfService}</Link>
            <Link to="/return-policy">{t.footer.returnPolicy}</Link>
            <Link to="/cookie-policy">{t.footer.cookiePolicy}</Link>
          </div>
          <div className={styles.copyright}>
            © {new Date().getFullYear()} Polaris - {t.footer.aPolysanProduct}
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}
